import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes, { func, string } from 'prop-types';

import Icon from '@/components/Base/Icon';

import {
  ClearValuesWrapper,
  ClearWrapper,
  IconsWrapper,
  InputStyles,
  InputWrapper,
  Options,
  OptionsList,
  Placeholder,
  PlaceholderMoreText,
  ToggleBtn,
  ToggleBtnLabel,
  Wrapper,
} from './SelectWrapper.styles';

function SelectWrapper({
  optionsLength = 0,
  placeholder = '',
  valuesLength = 0,
  renderValue = '',
  clearSelectedValues,
  children,
  type = 'checkbox',
  ...rest
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(null);

  const { formatMessage } = useIntl();

  const wrapperRef = useRef();
  const autoCompleteInputRef = useRef();

  const handleDocumentClick = (event) => {
    const targetElement = event.target;

    if (
      !wrapperRef.current?.contains(targetElement) &&
      wrapperRef.current !== targetElement
    ) {
      setIsOpen(false);
      setSearch(null);
    }
  };

  const handleDropdownClick = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const isSelectedAll = useMemo(
    () => valuesLength === optionsLength,
    [optionsLength, valuesLength]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch(null);
  };

  const handleOptionClick = () => {
    setTimeout(() => {
      if (type === 'radio') {
        setIsOpen(false);
        setSearch(null);
      }
    });
  };

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    if (autoCompleteInputRef.current) {
      autoCompleteInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <Wrapper ref={wrapperRef} {...rest}>
      <ToggleBtn
        data-empty={!renderValue}
        data-active={isOpen}
        type="button"
        onClick={handleDropdownClick}
      >
        <ToggleBtnLabel>
          {isSelectedAll ? formatMessage({ id: 'select.all' }) : renderValue}

          {valuesLength > 1 && !isSelectedAll && (
            <PlaceholderMoreText>
              {formatMessage({
                id: 'select.andMore',
              })}

              {` + ${valuesLength - 1}`}
            </PlaceholderMoreText>
          )}
        </ToggleBtnLabel>

        <Placeholder>{placeholder}</Placeholder>

        <IconsWrapper>
          {!!valuesLength && type !== 'radio' && (
            <ClearValuesWrapper onClick={clearSelectedValues}>
              <Icon name="clear" />
            </ClearValuesWrapper>
          )}

          <Icon name="dropdownArrow" />
        </IconsWrapper>
      </ToggleBtn>

      {isOpen && (
        <Options>
          {optionsLength > 5 && (
            <InputWrapper data-has-text={!!search}>
              <Icon name="search" />

              <InputStyles
                name="search"
                value={search || ''}
                ref={autoCompleteInputRef}
                placeholder={formatMessage({
                  id: 'search',
                })}
                onChange={handleSearch}
              />

              <ClearWrapper onClick={clearSearch} data-has-search={!!search}>
                <Icon name="clear" />
              </ClearWrapper>
            </InputWrapper>
          )}

          <OptionsList onClick={handleOptionClick}>
            {children({ search })}
          </OptionsList>
        </Options>
      )}
    </Wrapper>
  );
}

SelectWrapper.propTypes = {
  optionsLength: PropTypes.number,
  valuesLength: PropTypes.number,
  placeholder: string,
  renderValue: string,
  clearSelectedValues: func.isRequired,
  children: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['radio', 'checkbox']),
};

export default memo(SelectWrapper);
